import React, { useMemo, useState } from 'react';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import Button from '~/components/core/Atomic/Buttons/Button';
import DocumentsContainer from '~/components/Documents/DocumentsContainer';
import type { ClaimPolymorph } from '~/components/types/claim-types';

type ClaimDocument = NonNullable<ClaimPolymorph['documents']>[number];

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (document: ClaimDocument) => void;
  /**
   * Document IDs already chosen for pending (unsaved) invoice rows on the DA table.
   * Excluded from the picker so the user can't pick the same document twice — the BE
   * also rejects this on Save, but filtering here avoids the bad state in the first place.
   */
  excludeDocumentIds?: Set<number>;
}

/**
 * Document picker for the DA-screen "Add invoice" flow (invoice-summary granularity).
 * Only shows claim documents that aren't already attached to an invoice — picking one
 * passes the document to the caller, which seeds a new row on the DA table without
 * any BE call. The eventual Save creates the invoice and the row in one transaction.
 */
const AddInvoiceForExposureDialog: React.FC<Props> = ({ open, onClose, onSelect, excludeDocumentIds }) => {
  const { claim, onAsyncClaimUpdate } = useClaim({ claimRequired: true });
  const { classes } = useStyles();
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | undefined>();

  const claimWithUnattachedDocsOnly = useMemo(() => {
    if (!claim) return claim;
    const documents = (claim.documents ?? []).filter(
      (d: ClaimDocument) =>
        !d.invoice_id && !d.is_uploading && !d.is_removed && !(excludeDocumentIds?.has(d.id) ?? false)
    );
    return { ...claim, documents };
  }, [claim, excludeDocumentIds]);

  if (!open) return null;

  const handleSelect = () => {
    if (!selectedDocumentId) return;
    const doc = (claimWithUnattachedDocsOnly.documents ?? []).find((d: ClaimDocument) => d.id === selectedDocumentId);
    if (!doc) return;
    onSelect(doc);
    setSelectedDocumentId(undefined);
  };

  return (
    <CardDialog title="Select document for invoice" fullWidth isDialog maxWidth="lg" onClose={onClose}>
      <DocumentsContainer
        classes={classes}
        claim={claimWithUnattachedDocsOnly}
        selectedDocumentIds={selectedDocumentId ? [selectedDocumentId] : []}
        disableChooseMultiple
        onDocumentsClick={(documentIds: number[]) => setSelectedDocumentId(documentIds[0])}
        onNewDocumentCreated={async (documentId: number | null) => {
          await onAsyncClaimUpdate();
          if (documentId) setSelectedDocumentId(documentId);
        }}
        autoPaginateRowsPerPage={25}
      />
      <div className={classes.buttonsContainer}>
        <Button variant="contained" color="primary" onClick={handleSelect} disabled={!selectedDocumentId}>
          Select
        </Button>
      </div>
    </CardDialog>
  );
};

export default AddInvoiceForExposureDialog;
